import { useEffect, useState } from 'react'

type Path = number[]
interface Node {
  label: string
  path: string
  open: boolean
  isOpen?: boolean // whether this node is open (folder)
  checked?: number // 0: unchecked, 1: checked, 0.5: some checked
  children?: Node[] // children nodes
}

interface TreeState extends Node {
  _id: number // unique id for each node, used for React key
  children?: TreeState[] // children nodes
}

interface Event {
  type: string // event type
  path: Path | null
  params: unknown[] // additional parameters for the event
}

type Reducer = (state: TreeState, path: Path | null, ...args: any[]) => TreeState

const deepClone = <T>(x: T): T => JSON.parse(JSON.stringify(x))

export const findTargetNode = (root: Node, path: Path | null) => {
  let curNode = root
  for (const idx of path || []) {
    const { children = [] } = curNode
    if (!children[idx]) {
      throw new Error('finding node failed: invalid path!!')
    }
    curNode = children[idx]
  }

  return curNode
}

// deepclone the initial data for internal use, and assign uniq ids to each node
// deepclone only happens once at initialization, other operations will be in-place
const initStateWithUniqIds = (rootNode: Node): TreeState => {
  let curId = 0

  const _addId = (node: Node): TreeState => {
    const { children, ...state } = node

    const newNode: TreeState = {
      ...state,
      _id: curId++,
    }

    if (children) {
      newNode.children = children.map(_addId)
    }

    return newNode
  }

  return _addId(deepClone(rootNode))
}

// recursively set status for this node and all children, in place
const setStatusDown = (node: TreeState, status: number): TreeState => {
  node.checked = status
  const { children } = node
  if (children) {
    for (const child of children) {
      setStatusDown(child, status)
    }
  }
  return { ...node }
}

// set checked status for all nodes
const setAllCheckedStatus = setStatusDown

// calculate the check status of a node based on the check status of it's children
const getNewCheckStatus = (node: Node) => {
  const { children } = node

  if (!children?.length) {
    return node?.checked ?? 0
  }

  const sum = children.reduce((acc, c) => acc + (c.checked ?? 0), 0)

  if (sum === children.length) {
    return 1 // all checked
  }

  if (sum === 0) {
    return 0 // all unchecked
  }

  return 0.5 // some checked
}

// recursively update check status up
const updateStatusUp = (nodes: Node[]) => {
  const curNode = nodes.pop()

  if (!curNode) return

  curNode.checked = getNewCheckStatus(curNode)

  updateStatusUp(nodes)
}

// handle state change when user (un)check a TreeNode
const checkNode: Reducer = (rootNode: TreeState, path: Path | null, status: number): TreeState => {
  let curNode = rootNode
  const parentNodes = [curNode] // parent nodes for getNewCheckStatus() upwards

  for (const idx of path || []) {
    const node = curNode?.children?.[idx]

    if (node) {
      parentNodes.push(node)
      curNode = node
    }
  }

  setStatusDown(curNode, status) // update check status of this node and all childrens, in place

  parentNodes.pop() // don't need to check this node's level
  updateStatusUp(parentNodes) // update check status up, from this nodes parent, in place

  return { ...rootNode }
}

const toggleOpen = (rootNode: TreeState, path: Path | null, isOpen: boolean): TreeState => {
  if (!path) {
    throw new Error('path not provided for toggleOpen!!')
  }

  const targetNode = findTargetNode(rootNode, path)

  if (!targetNode.children) {
    throw new Error('only parent node (folder) can be opened!!')
  }

  targetNode.isOpen = isOpen

  return { ...rootNode }
}

const setAllOpenStatus = (node: TreeState, isOpen: boolean): TreeState => {
  const newNode = { ...node }
  const { children } = newNode

  if (children) {
    newNode.isOpen = isOpen
    newNode.children = children.map(child => setAllOpenStatus(child, isOpen))
  }

  return newNode
}

const getEvent = (eventName: string, path: Path | null, ...params: any[]): Event => ({
  type: eventName,
  path,
  params,
})

const initializeTreeState = (data: Node): TreeState =>
  setAllOpenStatus(setAllCheckedStatus(initStateWithUniqIds(data), 0), true)

const findAllTargetPathByProp = (root: Node, propName: keyof Node, targetVal: unknown) => {
  const res: Path[] = []

  const _traverse = (node: Node, curPath: Path) => {
    if (node[propName] === targetVal) {
      res.push(deepClone(curPath))
    }

    const { children } = node

    if (children) {
      children.forEach((n, i) => {
        _traverse(n, [...curPath, i])
      })
    }
  }

  _traverse(root, [])

  return res
}

export const findTargetPathByProp = (root: Node, propName: keyof Node, targetVal: unknown) => {
  const allPaths = findAllTargetPathByProp(root, propName, targetVal)
  return allPaths.length > 0 ? allPaths[0] : null
}

interface UseTreeStateOptions<R extends Record<string, Reducer>> {
  data: Node
  onChange?: (treeState: Node, event: any) => void
  customReducers?: R
}

interface State<R extends Record<string, Reducer>> {
  treeState: TreeState | null
  reducers: {
    [K in keyof R]: (path: Path | null, ...args: any[]) => null | undefined
  } & {
    checkNodeByProp: (propName: keyof Node, targetValue: unknown, ...args: any[]) => void
    toggleOpenByProp: (propName: keyof Node, targetValue: unknown, ...args: any[]) => void
    setTreeState: (state: TreeState) => void
    checkNode: (path: Path | null, ...args: any[]) => null | undefined
    toggleOpen: (path: Path | null, ...args: any[]) => null | undefined
  }
}

export const useTreeState = <R extends Record<string, Reducer>>({
  data,
  onChange,
  customReducers,
}: UseTreeStateOptions<R>): State<R> => {
  const [treeState, setTreeState] = useState<TreeState | null>(null)
  const [event, setEvent] = useState<Event>({
    type: 'initialization',
    path: null,
    params: [],
  })

  useEffect(() => {
    const initState = initializeTreeState(data)
    setTreeState(initState)
  }, [])

  useEffect(() => {
    if (typeof onChange === 'function' && treeState && event) {
      onChange(treeState, event)
    }
  }, [treeState, event])

  const _setTreeState = (state: TreeState) => {
    const e = getEvent('setTreeState', null, state)

    setEvent(e)
    setTreeState(state)
  }

  const getExternalReducer =
    (reducer: Reducer, name: string) =>
    (path: Path | null, ...args) => {
      if (!treeState) {
        console.warn(`useTreeState: ${name} called before treeState is initialized.`)
        return null
      }

      const _path = path ? [...path] : null
      const e = getEvent(name, _path, ...args)

      const newState = reducer(treeState, _path, ...args)

      setEvent(e)
      setTreeState(newState)
    }

  const getReducerByProp =
    (reducer: (...args: any[]) => void) =>
    (propName: keyof Node, targetValue: unknown, ...args) => {
      if (!treeState) {
        return
      }

      const path = findTargetPathByProp(treeState, propName, targetValue)

      if (!path) {
        return
      }

      reducer(path, ...args)
    }

  const _customReducers = Object.fromEntries(
    Object.entries(customReducers ?? {}).map(([name, f]) => [name, getExternalReducer(f, name)]),
  ) as {
    [K in keyof R]: (path: Path | null, ...args: any[]) => null | undefined
  }

  const predefinedReducers = {
    setTreeState: _setTreeState,
    checkNode: getExternalReducer(checkNode, 'checkNode'),
    toggleOpen: getExternalReducer(toggleOpen, 'toggleOpen'),
  }

  const reducers = {
    ...predefinedReducers,
    checkNodeByProp: getReducerByProp(predefinedReducers.checkNode),
    toggleOpenByProp: getReducerByProp(predefinedReducers.toggleOpen),
    ..._customReducers,
  }

  return {
    treeState,
    reducers,
  }
}
